import _ from 'lodash';

import { mathCuttingFunction } from '@common/helpers/filters';
import { broadcast, broadcastChannels } from '../broadcast';
import { getRoundingMethod } from './roundingMethod';
import { swarmRequest } from '../util/requestMiddleware';

let roundMethod = _.noop;

export const setOddConverterPartnerConfig = (roundMethodType) => {
  roundMethod = getRoundingMethod(roundMethodType);
};

/**
 * Odd converter
 * returns a function to convert decimal odds to corresponding format
 * Usage:
 * OddConverter(odd, format)
 * e.g.  var coefficient = OddConverter(1.01, "fractional")
 */
const OddConverter = (function () {
  let ladder = null;
  let ladderKeys = null;

  async function socketStateChange(e) {
    if (e.detail?.connected) {
      const response = await swarmRequest({}, 'get_permissible_odds');
      if (response?.details) {
        convertLadddersFromSwarm(response);
        broadcast.removeEventListener(broadcastChannels.SocketStateChange, socketStateChange);
      }
    }
  }

  broadcast.addEventListener(broadcastChannels.SocketStateChange, socketStateChange);

  function convertLadddersFromSwarm(response) {
    ladder = {};
    
    if (Array.isArray(response?.details)) {
      response.details.forEach((item) => {
        ladder[item.Price] = `${item.Numerator}/${item.Denominator}`;
      });
    }

    ladderKeys = Object.keys(ladder).sort((a, b) => parseFloat(a) - parseFloat(b));
  }
  /**
     * Recursively finds the biggest decimal odd that is smaller than num
     * @param startIndex search start index
     * @param endIndex search end index
     * @param num decimal odd value to compare with
     */
  function findNearestLadderKey(startIndex, endIndex, num) {
    const middleIndex = startIndex + Math.ceil((endIndex - startIndex) / 2);
    if ((middleIndex === 0 && ladderKeys[middleIndex] > num) || (middleIndex === ladderKeys.length - 1 && ladderKeys[middleIndex] < num) || (ladderKeys[middleIndex] < num && num < ladderKeys[middleIndex + 1])) {
      return ladderKeys[middleIndex];
    }
    if (ladderKeys[middleIndex] < num) {
      return findNearestLadderKey(middleIndex, endIndex, num);
    }
    return findNearestLadderKey(startIndex, middleIndex - 1, num);
  }

  function dec2fracFromLadder(dec) {
    let val;
    if (ladder[dec]) {
      return ladder[dec];
    }
    val = findNearestLadderKey(0, ladderKeys.length - 1, dec);
    return ladder[val];
  }

  /**
     * Recursively converts odd to fractional format
     * @param {string} decVal odd value from swarm(decimal)
     * @returns {string} converted odd
     */
  function dec2frac(decVal) {
    let Znxt;
    let Dnxt;
    let Nnxt;
    function recCalc(Zcur, Dcur, Dprev) {
      Dcur = Dcur !== undefined ? Dcur : 1;
      Dprev = Dprev !== undefined ? Dprev : 0;
      Znxt = 1 / (Zcur - parseInt(Zcur, 10));
      Dnxt = Dcur * parseInt(Znxt, 10) + Dprev;
      Nnxt = Math.round(decVal * Dnxt);

      return (Nnxt / Dnxt === decVal) ? `${Nnxt.toString()}/${Dnxt.toString()}` : recCalc(Znxt, Dnxt, Dcur);
    }

    // Use this casting method because of JS number bug for example "2.2 - 1 = 1.1999(9)"
    if (decVal !== parseInt(decVal, 10)) {
      decVal = parseFloat(`${(parseInt(decVal, 10) - 1).toString()}.${String(decVal).split('.')[1]}`);
    } else {
      decVal -= 1;
    }

    return decVal % 1 === 0 ? `${String(decVal)}/1` : String(recCalc(decVal));
  }

  /**
     * possible formats.  the first one will be used as default if no valid format is found in config
     * @type {Array}
     */
  const possibleFormats = ['decimal', 'fractional', 'american', 'hongkong', 'malay', 'indo'];

  /**
     * Converts odd to specified format
     * @param {string} value odd value from swarm(decimal)
     * @param {string} format destination format
     * @returns {string} converted odd
     */

  function convert(config, value, format, type) {
    // TODO: refactor
    if (format === 'fractional' && config.odd.useLadderForFractionalFormat && type === 'fictional' && value !== undefined) // use it to calculate express odds as you see on bet365 :)
    { return `${Math.round(parseFloat(value - 1) * 100 || 0) / 100}/1`; }

    const fValue = parseFloat(value);
    const iValue = parseInt(value, 10);
    const rValue = (value !== undefined && value !== '') ? Math.round(fValue * 100 || 0) / 100 : value;
    switch (format) {
      case 'decimal':
        if (value === undefined || value === '') {
          return value;
        }
        // if (config.main.oddDecimalFormat2Digits) {
        //     return (mathCuttingFunction(fValue * 10 * 10) / 100).toFixed(2); // 10 * 10 because javascript have bug 8.2 * 100 = 819.9999999999999
        // }
        return (iValue !== fValue && value.toString().split('.')[1] && value.toString().split('.')[1].length > 2) ? roundMethod(value, config.odd.roundDecimalCoefficients) : fValue.toFixed(2);
      case 'fractional':
        return value ? (config.odd.useLadderForFractionalFormat ? dec2fracFromLadder(fValue) : dec2frac(rValue)) : value;
      case 'american':
        return value ? rValue >= 2 ? `+${Math.round(100 * (rValue - 1))}` : rValue !== 1 ? Math.round(-100 / (rValue - 1)) : '-' : rValue;
      case 'hongkong': {
        let hValue;
        if (value !== undefined && value !== '') {
          if (iValue !== fValue && value.toString().split('.')[1]?.length > 2) {
            hValue = Math.round((value - 1) * 10 ** config.odd.roundDecimalCoefficients) / 10 ** config.odd.roundDecimalCoefficients;
          } else {
            hValue = (fValue - 1).toFixed(2);
          }
        } else {
          hValue = value;
        }
        if (config.odd.oddDecimalFormat2Digits) {
          hValue = (mathCuttingFunction(hValue * 10 * 10) / 100).toFixed(2);
        }
        return hValue;
      }
      case 'malay':
        if (fValue === 2) {
          return '1.000';
        } if (fValue > 2) {
          return (Math.round(((1 / (1 - fValue)).toFixed(config.odd.roundDecimalCoefficients + 1)) * 10 ** config.odd.roundDecimalCoefficients) / 10 ** config.odd.roundDecimalCoefficients).toFixed(config.odd.roundDecimalCoefficients);
        }
        return (fValue - 1).toFixed(config.odd.roundDecimalCoefficients);

      case 'indo':
        if (fValue === 2) {
          return '1.000';
        } if (fValue > 2) {
          return (fValue - 1).toFixed(config.odd.roundDecimalCoefficients);
        }
        return (Math.round(((1 / (1 - fValue)).toFixed(config.odd.roundDecimalCoefficients + 1)) * 10 ** config.odd.roundDecimalCoefficients) / 10 ** config.odd.roundDecimalCoefficients).toFixed(config.odd.roundDecimalCoefficients);

      default:
        return rValue;
    }
  }

  const convertMemo = _.memoize(convert, (...args) => args.join('_'));

  return function (config, value, format, type = null, displayKey, doesNeedOriginal) {
    // if (!ladder || !ladderKeys) return null;
    format = format || config.odd.oddsFormat;
    if (value === null || value === undefined || isNaN(value)) {
      return value;
    }
    if (value === 1) {
      return null;
    }
    if (config.odd.specialOddFormat && config.odd.specialOddFormat[format]) {
      if (!doesNeedOriginal) {
        format = config.odd.specialOddFormat[format].displayKey[displayKey] || config.odd.specialOddFormat[format].default;
      } else if (config.odd.specialOddFormat[format].originalOddType) {
        format = config.odd.specialOddFormat[format].originalOddType;
      }
    }

    if (!possibleFormats.includes(format)) // select default format if current one is invalid
    { format = possibleFormats[0]; }

    return convertMemo(config, value, format, type);
  };
}());

export default OddConverter;
